/**
 * Seeded PRNG — spec §11 rule 1.
 *
 * xoshiro128**, chosen because every operation is a 32-bit integer op with no
 * platform-dependent behaviour. State lives IN GameState and is threaded through
 * explicitly; there is deliberately no module-level instance, because a hidden
 * generator is exactly what makes a snapshot irreproducible.
 *
 * Two calling styles, both pure with respect to GameState:
 *
 *   const [n, rng] = nextUint32(state.rng);        // single draw
 *
 *   const c = cursor(state.rng);                   // many draws
 *   c.int(0, 10); c.int(0, 10);
 *   state.rng = c.commit();
 *
 * The cursor is a local scratch object. It is never stored in state.
 *
 * TRANSCENDENTALS ARE BANNED IN THE SIM. Math.log, Math.exp, Math.pow, and the
 * trig functions are not correctly-rounded by the ECMAScript spec — two engines
 * may return results differing in the last bit, which would fork the golden
 * hash. Math.sqrt is safe (IEEE 754 requires exact rounding). Where a
 * distribution needs a curve, build it from integer arithmetic — see
 * `biasMultiplierBp` below.
 */

import type { RngState } from './types';

const UINT32 = 0x100000000;

function rotl(x: number, k: number): number {
  return ((x << k) | (x >>> (32 - k))) >>> 0;
}

/**
 * splitmix32 — used only to expand a single seed into four state words.
 * Returns [draw, successorSeed].
 */
function splitmix32(z: number): [number, number] {
  const next = (z + 0x9e3779b9) >>> 0;
  let t = next;
  t = Math.imul(t ^ (t >>> 16), 0x21f0aaad) >>> 0;
  t = Math.imul(t ^ (t >>> 15), 0x735a2d97) >>> 0;
  t = (t ^ (t >>> 15)) >>> 0;
  return [t, next];
}

/** Build an RngState from an integer seed. Never returns the all-zero state. */
export function createRng(seed: number): RngState {
  const [a, z1] = splitmix32(seed >>> 0);
  const [b, z2] = splitmix32(z1);
  const [c, z3] = splitmix32(z2);
  const [d] = splitmix32(z3);

  if ((a | b | c | d) === 0) {
    // Degenerate state; xoshiro would emit zeros forever.
    return { a: 0x9e3779b9, b: 0x243f6a88, c: 0xb7e15162, d: 0x85a308d3 };
  }
  return { a, b, c, d };
}

/** Advance the generator. Returns the draw and the successor state. */
export function nextUint32(rng: RngState): [number, RngState] {
  let s0 = rng.a;
  let s1 = rng.b;
  let s2 = rng.c;
  let s3 = rng.d;

  const result = Math.imul(rotl(Math.imul(s1, 5) >>> 0, 7), 9) >>> 0;
  const t = (s1 << 9) >>> 0;

  s2 = (s2 ^ s0) >>> 0;
  s3 = (s3 ^ s1) >>> 0;
  s1 = (s1 ^ s2) >>> 0;
  s0 = (s0 ^ s3) >>> 0;
  s2 = (s2 ^ t) >>> 0;
  s3 = rotl(s3, 11);

  return [result, { a: s0, b: s1, c: s2, d: s3 }];
}

/**
 * A scratch cursor for call sites that draw many values (world generation,
 * mostly). Wraps the threading so the caller does not have to rebind a state
 * variable on every line. Commit the result back into GameState when done.
 */
export interface RngCursor {
  /** Raw 32-bit draw. */
  uint32(): number;
  /** Uniform integer in [min, max] inclusive. */
  int(min: number, max: number): number;
  /** Uniform value in [0, 1). Transient only — never store this in state. */
  unit(): number;
  /** Multiplier in basis points, log-normal-ish. See biasMultiplierBp. */
  biasBp(minBp: number, maxBp: number): number;
  /** The successor state after every draw made through this cursor. */
  commit(): RngState;
}

export function cursor(initial: RngState): RngCursor {
  let state = initial;

  const uint32 = (): number => {
    const [value, next] = nextUint32(state);
    state = next;
    return value;
  };

  const int = (min: number, max: number): number => {
    if (max < min) throw new Error(`rng.int: max ${max} < min ${min}`);
    const span = max - min + 1;
    // Rejection sampling keeps the distribution exactly uniform. The bound is a
    // multiple of span, so the loop terminates with probability 1 and in
    // practice almost always on the first draw.
    const limit = UINT32 - (UINT32 % span);
    let draw = uint32();
    while (draw >= limit) draw = uint32();
    return min + (draw % span);
  };

  const unit = (): number => uint32() / UINT32;

  const biasBp = (minBp: number, maxBp: number): number =>
    biasMultiplierBp(int(0, 0xffffffff >>> 0), minBp, maxBp);

  return { uint32, int, unit, biasBp, commit: () => state };
}

/**
 * Skewed multiplier in basis points (10000 = 1.0x), for planetary abundance.
 *
 * Spec §6 asks for "a log-normal-ish distribution (range roughly 0.2x to 3x)".
 * Math.log/Math.exp are banned here (see the file header), so the skew is built
 * from integer arithmetic instead: cubing a uniform biases hard toward the low
 * end, and the result is mapped across the requested range. The shape is not
 * literally log-normal, but it delivers what §6 actually wants — most worlds
 * mediocre in a given mineral, a few strikingly rich — and it is bit-identical
 * on every engine.
 */
export function biasMultiplierBp(draw: number, minBp: number, maxBp: number): number {
  // u in [0, 10000)
  const u = (draw >>> 0) % 10000;
  // Cube in a widened integer domain, then scale back. u^3 / 10000^2.
  const cubed = Math.floor((u * u * u) / (10000 * 10000));
  return minBp + Math.floor((cubed * (maxBp - minBp)) / 10000);
}
